import { Flex, Text } from '@chakra-ui/react';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  ButtonText,
  FormFeedback,
  OutlineCard,
  TagInput,
  TagInputProps
} from '@morressier/alembic';
import { useEffect, useState } from 'react';
import { useFieldArray, useForm, useFormContext } from 'react-hook-form';
import { FieldLabel } from 'src/components/Field';
import { FormFieldFunding } from '../../../../../../../../domain/types/article';
import { RadioGroup } from 'src/components/RadioGroup';
import { get } from 'lodash';
import FunderSearch, { IFunder } from './FunderSearch';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';
import { IDependentValue } from '@publishing-engine/domain/models';

export type IGrantNumbers = { text: string }[];
export interface IFundingGrantNumbersProps {
  initialValues?: IGrantNumbers;
  onValueChange: (grantNumbers: IGrantNumbers) => void;
  onInvalidChange?: (isInvalid: boolean) => void;
}
export type ExcludedTagInputProps = Omit<TagInputProps, 'name' | 'control'>;

export const FundingGrantNumbers = ({
  onValueChange,
  initialValues,
  onInvalidChange,
  ...props
}: IFundingGrantNumbersProps & ExcludedTagInputProps) => {
  const { control, watch } = useForm<{ grantNumbers?: IGrantNumbers }>({
    defaultValues: { grantNumbers: initialValues }
  });

  const grantNumbers = watch('grantNumbers');
  const hasInvalidGrantNumber = grantNumbers
    ? grantNumbers.some(number => number.text.trim().length === 0)
    : false;

  useEffect(() => {
    if (grantNumbers !== undefined) onValueChange(grantNumbers);
  }, [grantNumbers]);

  useEffect(() => {
    onInvalidChange?.(Boolean(hasInvalidGrantNumber));
  }, [hasInvalidGrantNumber, onInvalidChange]);

  return (
    <TagInput
      {...props}
      isInvalid={Boolean(hasInvalidGrantNumber)}
      name="grantNumbers"
      errorMessage={
        grantNumbers?.some(item => item?.text?.trim() === '')
          ? 'Whitespace not allowed'
          : props.errorMessage
          ? props.errorMessage
          : null
      }
      control={control}
      helperText="Press Enter to add grant or award numbers"
      placeholder="Enter grant or award numbers"
    />
  );
};

export type IFundingValue = {
  metadata: {
    funding: IDependentValue<
      {
        fundingBody: string;
        grantNumber: string;
      }[]
    >;
  };
};

const Funding = ({
  config,
  onGrantNumbersInvalidChange
}: {
  config: FormFieldFunding;
  onGrantNumbersInvalidChange?: (isInvalid: boolean) => void;
}) => {
  const {
    formState: { errors },
    control,
    register,
    watch,
    clearErrors,
    setValue
  } = useFormContext<IFundingValue>();
  const {
    params: { validations, labels },
    caseDataFields: { main: field, dependsOn }
  } = config;
  const dependsOnField = `${field}.${dependsOn}` as const;
  const mainField = `${field}.value` as const;
  const error = get(errors, field);
  const [invalidGrantNumbersByRow, setInvalidGrantNumbersByRow] = useState<
    Record<string, boolean>
  >({});

  const funders = watch(mainField);
  const isFunded = watch(dependsOnField);
  const { fields, append, remove, update } = useFieldArray({
    control,
    name: mainField
  });

  const addFundingRow = () => {
    clearErrors(field);
    append({ fundingBody: '', grantNumber: '' });
  };

  useEffect(() => {
    onGrantNumbersInvalidChange?.(
      Object.values(invalidGrantNumbersByRow).some(Boolean)
    );
  }, [invalidGrantNumbersByRow, onGrantNumbersInvalidChange]);

  useEffect(() => {
    if (!isFunded) {
      setInvalidGrantNumbersByRow({});
      onGrantNumbersInvalidChange?.(false);
    }
  }, [isFunded, onGrantNumbersInvalidChange]);

  if (isFunded && !fields.length) {
    addFundingRow();
  }

  const handleGrantNumberChange = (
    grantNumbers: IGrantNumbers,
    index: number
  ) => {
    const grantNumber = grantNumbers?.map(({ text }) => text).join(',') || '';

    setValue(`${mainField}.${index}.grantNumber`, grantNumber, {
      shouldValidate: !!grantNumbers?.length
    });
  };

  const handleGrantNumberValidityChange = (
    rowId: string,
    isInvalid: boolean
  ) => {
    setInvalidGrantNumbersByRow(prev => ({
      ...prev,
      [rowId]: isInvalid
    }));
  };

  useEffect(() => {
    // Clear the funding information when 'isFunded' is false
    if (!isFunded) {
      setValue(mainField, []); // Set the funders array to an empty array
      clearErrors(mainField);
    }
  }, [isFunded, setValue, clearErrors, mainField]);

  const { submissionContext } = useSubmissionContextData();
  const submissionPreferences =
    submissionContext?.config.cms.authorSubmissionForm.submissionPreferences;

  return (
    <Flex flexDirection="column" gap={8} alignItems="flex-start">
      <Flex flexDirection="column" gap={2}>
        <Text textStyle="headingSection" data-cy="funding-section-title">
          {submissionPreferences?.funding.mainLabel ??
            'Is there Funding to report for this submission?'}
        </Text>
        <Text textStyle="textBody" data-cy="funding-section-explanation">
          {submissionPreferences?.funding.secondaryLabel ??
            'Including details about your research funders and associated grants or awards helps the editorial team assess your manuscript’s eligibility for transformative agreements. This information can streamline the review process and ensure compliance with funding requirements.'}
        </Text>
      </Flex>

      <OutlineCard borderless padding={8}>
        <Flex flexDirection="column" gap={6} alignItems="flex-start">
          <FieldLabel
            isRequired={validations.required}
            label={labels.main}
            subText={labels.subtitle}
          />

          <Flex flexDirection="column" gap={4}>
            <RadioGroup
              name={dependsOnField}
              control={control}
              options={[
                {
                  value: false,
                  children: 'No'
                },
                {
                  value: true,
                  children: 'Yes'
                }
              ]}
            />
          </Flex>

          {isFunded ? (
            <>
              <Text textStyle="headingItem" data-cy="funder-list-title">
                Funder List
              </Text>
              {/* --- Funding rows --- */}

              {fields.map((field, index) => (
                <Flex
                  key={field.id}
                  flexDirection="column"
                  gap={8}
                  alignItems="flex-start"
                  padding={8}
                  width="100%"
                  bgColor="surfaceSecondaryFill"
                >
                  <Flex flexDirection="column" gap={2} width="100%">
                    <FieldLabel
                      isRequired
                      label={labels.section.title}
                      subText={labels.section.explanation}
                    />
                    <Flex gap={6} flexDirection="column">
                      <Flex flexDirection="column" gap={2}>
                        <FunderSearch
                          {...register(`${mainField}.${index}.fundingBody`)}
                          isInvalid={!!error?.value?.[index]?.fundingBody}
                          placeholder="Search for funders"
                          onFunderSelect={(funder: IFunder) => {
                            update(index, {
                              ...field,
                              fundingBody: funder.name
                            });
                          }}
                          onAddUnverifiedFunder={() => {
                            clearErrors(`${mainField}.${index}.fundingBody`);
                          }}
                          data-cy={`funder-search-${index}`}
                        />
                        {error?.value?.[index]?.fundingBody && (
                          <FormFeedback variant="error">
                            Funding Body is required
                          </FormFeedback>
                        )}
                      </Flex>

                      {funders?.[index]?.fundingBody && (
                        <Flex flexDirection="column" gap={2}>
                          <FieldLabel
                            label="Grant or Award Numbers"
                            subText="Add grant or award numbers one by one: type and press Enter after each."
                          />

                          <FundingGrantNumbers
                          initialValues={
                              funders[index].grantNumber
                                ? funders[index].grantNumber
                                    .split(',')
                                    .map(v => ({ text: v }))
                                : undefined
                            }
                            onValueChange={grantNumbers =>
                              handleGrantNumberChange(grantNumbers, index)
                            }
                            onInvalidChange={isInvalid =>
                              handleGrantNumberValidityChange(field.id, isInvalid)
                            }
                            errorMessage={
                              error?.value?.[index]?.grantNumber
                                ? 'Grant or Award Number is required'
                                : null
                            }
                          />
                        </Flex>
                      )}

                      <ButtonText
                        alignSelf={'flex-end'}
                        iconLeft={<FontAwesomeIcon icon={faTrash} />}
                        onMouseDown={e => e.preventDefault()}
                        onClick={() => {
                          setInvalidGrantNumbersByRow(prev => {
                            const next = { ...prev };
                            delete next[field.id];
                            return next;
                          });
                          remove(index);
                        }}
                        marginLeft="auto"
                        data-cy={`delete-funder-button-${index}`}
                      >
                        Delete Funder
                      </ButtonText>
                    </Flex>
                  </Flex>

                  {error?.['root'] ? (
                    <FormFeedback
                      variant="error"
                      data-cy="funding-error-message"
                    >
                      Please provide funding data to proceed.
                    </FormFeedback>
                  ) : null}
                </Flex>
              ))}

              <Flex alignItems="center" width="100%">
                {error?.[dependsOn] ? (
                  <FormFeedback variant="error">
                    {error?.[dependsOn].message}
                  </FormFeedback>
                ) : null}

                <ButtonText
                  alignSelf={'flex-end'}
                  iconLeft={<FontAwesomeIcon icon={faPlus} />}
                  onClick={addFundingRow}
                  marginLeft="auto"
                  data-cy="add-funder-button"
                >
                  Add {fields.length > 0 ? 'another' : ''} fund
                </ButtonText>
              </Flex>
            </>
          ) : null}

          {error?.[dependsOn] ? (
            <FormFeedback variant="error">
              Please indicate if there is funding to report for this submission.
            </FormFeedback>
          ) : null}
        </Flex>
      </OutlineCard>
    </Flex>
  );
};

export default Funding;
